<template>
  <div v-if="authModalState.open" class="login-modal" @click.self="close">
    <div class="login-modal-card">
      <button class="login-modal-close" type="button" aria-label="닫기" @click="close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18"/><path d="M6 6l12 12"/></svg>
      </button>

      <img class="login-modal-logo" src="@/assets/logo.png" alt="surveasy" width="120">

      <p class="login-modal-title">로그인</p>
      <p class="login-modal-sub">서베이지에서 설문을 주문하고 진행 상황을 확인하세요.</p>

      <div class="login-modal-form">
        <input
          class="login-modal-input"
          type="email"
          v-model="email"
          placeholder="이메일"
          @keyup.enter="focusPassword"
        >
        <input
          ref="passwordInput"
          class="login-modal-input"
          type="password"
          v-model="password"
          placeholder="비밀번호"
          @keyup.enter="logIn"
        >
      </div>

      <button class="login-modal-submit" type="button" :disabled="submitting" @click="logIn">
        {{ submitting ? '로그인 중…' : '로그인' }}
      </button>

      <button class="login-modal-join" type="button" @click="goJoin">회원가입</button>
    </div>
  </div>
</template>

<script>
import { instance } from '../api/index'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'
import VueCookies from 'vue-cookies'
import { authModalState } from '../plugins/auth-modal'
import { showAlert } from '../plugins/dialog'

export default {
  name: 'LoginModal',
  data() {
    return {
      authModalState,
      email: '',
      password: '',
      submitting: false,
      admin: 'admin@test.com'
    }
  },
  watch: {
    'authModalState.open'(open) {
      document.documentElement.style.overflow = open ? 'hidden' : ''
      document.body.style.overflow = open ? 'hidden' : ''
      if (!open) {
        this.email = ''
        this.password = ''
        this.submitting = false
      }
    }
  },
  beforeUnmount() {
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
  },
  methods: {
    close() {
      if (authModalState.resolve) authModalState.resolve(false)
      authModalState.open = false
    },
    focusPassword() {
      this.$refs.passwordInput && this.$refs.passwordInput.focus()
    },
    goJoin() {
      this.close()
      this.$router.push('/join')
    },
    async logIn() {
      if (this.submitting) return

      if (this.email == '') {
        await showAlert('이메일을 입력하세요')
        return
      } else if (this.password == '') {
        await showAlert('비밀번호를 입력하세요')
        return
      }

      this.submitting = true

      if (this.email == this.admin) {
        try {
          const response = await instance.post('/user/signin', {
            email: this.email,
            password: this.password
          })
          localStorage.setItem('access_token', response.data.accessToken)
          VueCookies.set('refresh_token', response.data.refreshToken)
          this.$store.dispatch('setCurrentUserAdmin')
          this.submitting = false
          if (authModalState.resolve) authModalState.resolve(true)
          authModalState.open = false
        } catch (error) {
          const status = error.response.status
          if (status == 404) {
            await showAlert('존재하지 않는 아이디입니다.')
          } else if (status == 400) {
            await showAlert('잘못된 비밀번호입니다.')
          } else {
            await showAlert('아이디와 비밀번호를 다시 확인해주세요')
          }
          this.submitting = false
        }
        return
      }

      const auth = getAuth()
      signInWithEmailAndPassword(auth, this.email, this.password)
        .then(() => {
          this.$store.dispatch('setCurrentUser', {
            payload: auth.currentUser.email
          })
          this.submitting = false
          if (authModalState.resolve) authModalState.resolve(true)
          authModalState.open = false
        })
        .catch(async (error) => {
          this.submitting = false
          await showAlert(this.$store.state.firebaseAuthErrorMsg[error.code] || '로그인에 실패했습니다.')
        })
    }
  }
}
</script>

<style scoped>
.login-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(20, 20, 20, 0.45);
  padding: 20px;
  box-sizing: border-box;
}

.login-modal-card {
  position: relative;
  width: 100%;
  max-width: 400px;
  padding: 40px 32px 32px;
  box-sizing: border-box;
  background-color: var(--surface-card);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  text-align: center;
  font-family: var(--font-sans);
}

.login-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: none;
  color: var(--ink-400);
  cursor: pointer;
}

.login-modal-close svg {
  width: 100%;
  height: 100%;
}

.login-modal-close:hover {
  color: var(--ink-700);
}

.login-modal-logo {
  margin-bottom: var(--space-6);
}

.login-modal-title {
  margin: 0 0 var(--space-2) 0;
  font-size: 22px;
  font-weight: var(--font-weight-bold);
  color: var(--text-heading);
}

.login-modal-sub {
  margin: 0 0 var(--space-8) 0;
  font-size: 14px;
  color: var(--text-muted);
}

.login-modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.login-modal-input {
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  font-family: var(--font-sans);
  font-size: 15px;
  color: var(--ink-800);
  background-color: var(--white);
  border: 1px solid var(--ink-200);
  border-radius: var(--radius-md);
}

.login-modal-input:focus {
  outline: none;
  border-color: var(--green-500);
}

.login-modal-submit {
  width: 100%;
  height: 48px;
  margin-bottom: var(--space-3);
  border: none;
  border-radius: var(--radius-md);
  background-color: var(--green-500);
  color: var(--white);
  font-size: 15px;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  font-family: var(--font-sans);
}

.login-modal-submit:hover {
  background-color: var(--green-600);
}

.login-modal-submit:disabled {
  background-color: var(--ink-200);
  cursor: default;
}

.login-modal-join {
  width: 100%;
  height: 48px;
  border: 1px solid var(--ink-200);
  border-radius: var(--radius-md);
  background-color: var(--white);
  color: var(--text-body);
  font-size: 15px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  font-family: var(--font-sans);
}

.login-modal-join:hover {
  border-color: var(--ink-400);
}
</style>
